import React, { useCallback, useRef, useState } from 'react';
import './ConfirmDialog.css';

const ConfirmDialog = ({ open, message, confirmLabel, cancelLabel, danger, onResolve }) => {
  if (!open) return null;

  return (
    <div className="confirm-overlay" role="presentation" onClick={() => onResolve(false)}>
      <div
        className="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="confirm-message">{message}</p>
        <div className="confirm-actions">
          <button type="button" className="btn btn-secondary" onClick={() => onResolve(false)}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            autoFocus
            onClick={() => onResolve(true)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// window.confirm() is silently ignored on iOS home-screen web apps, inside
// in-app browsers, and by any mobile browser that has suppressed dialogs for
// the page. It returns false immediately there, so a confirm-gated handler
// returns on its first line and the button looks dead. This renders the prompt
// as real DOM instead, which behaves the same on every device.
//
// Returns [confirm, dialog]: await confirm(...) resolves to true or false, and
// dialog must be rendered somewhere in the component's tree.
export const useConfirm = () => {
  const [pending, setPending] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        resolverRef.current = resolve;
        setPending(typeof options === 'string' ? { message: options } : options);
      }),
    []
  );

  const handleResolve = useCallback((result) => {
    setPending(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(result);
  }, []);

  const dialog = (
    <ConfirmDialog
      open={pending !== null}
      message={pending?.message}
      confirmLabel={pending?.confirmLabel ?? 'Delete'}
      cancelLabel={pending?.cancelLabel ?? 'Cancel'}
      danger={pending?.danger ?? true}
      onResolve={handleResolve}
    />
  );

  return [confirm, dialog];
};

export default ConfirmDialog;
